/**
 * Annulation depuis la fiche (SPEC FC13) : places d'une réservation et messages.
 *
 * Une réservation à deux = deux visites Mindbody du même client (sa place, puis celle de son invité).
 * Comme en v1 (CancelVisitModal : une visite par confirmation), chaque place s'annule par sa propre
 * requête `POST class/cancel` ; la fiche laisse choisir entre les deux places et la seule place de
 * l'invité, et dit précisément ce qui est annulé si une requête échoue après une autre.
 */
import type { PickaspotReservation } from 'types/class';
import type { ClientVisit } from 'types/client';

export interface CancelSeat {
  visit: ClientVisit;
  isGuest: boolean;
  spotNumber: number | null;
}

export type CancelScope = 'all' | 'guest';

type ReservationLike = Pick<PickaspotReservation, 'reservationExternalId' | 'reservationType'> & {
  spot?: Partial<PickaspotReservation['spot']> | null;
};

/**
 * Places de la réservation, celle du membre d'abord. Le spot d'une visite est la réservation Pick-a-Spot de
 * même `reservationExternalId` (= ClassesService::getReservation). L'invité est la réservation de type « guest »
 * si Mindbody le dit ; sinon la visite réservée en second (Id plus grand) : la fiche réserve toujours la place du
 * membre avant celle de l'invité.
 */
export function cancelSeats(visits: ClientVisit[], reservations: ReservationLike[] | null | undefined): CancelSeat[] {
  const byVisit = new Map<string, ReservationLike>();
  (reservations ?? []).forEach((r) => {
    if (r?.reservationExternalId !== undefined && r?.reservationExternalId !== null) byVisit.set(String(r.reservationExternalId), r);
  });

  const seats = [...visits]
    .sort((a, b) => a.Id - b.Id)
    .map((visit) => {
      const reservation = byVisit.get(String(visit.Id));
      return {
        visit,
        typedGuest: /guest/i.test(reservation?.reservationType ?? ''),
        spotNumber: reservation?.spot?.spotNumber ? Number(reservation.spot.spotNumber) : null,
      };
    });

  const typed = seats.some((s) => s.typedGuest) && seats.some((s) => !s.typedGuest);
  return seats
    .map((s, index) => ({ visit: s.visit, spotNumber: s.spotNumber, isGuest: typed ? s.typedGuest : index > 0 }))
    .sort((a, b) => Number(a.isGuest) - Number(b.isGuest));
}

/** Places à annuler selon le choix, invités d'abord : un échec en cours de route laisse au membre sa propre place. */
export function seatsToCancel(seats: CancelSeat[], scope: CancelScope): CancelSeat[] {
  const guests = seats.filter((s) => s.isGuest);
  if (scope === 'guest' && guests.length) return guests;
  return [...guests, ...seats.filter((s) => !s.isGuest)];
}

const spotSuffix = (seat: CancelSeat) => (seat.spotNumber ? ` (spot #${seat.spotNumber})` : '');

/** « La place de votre invité (spot #13) », « Votre place (spot #12) ». */
export function seatLabel(seat: CancelSeat): string {
  return `${seat.isGuest ? 'La place de votre invité' : 'Votre place'}${spotSuffix(seat)}`;
}

/** Spots, pour les options de la modale : « Spots #12 et #13 », « Spot #13 », '' sans plan. */
export function spotsText(seats: CancelSeat[]): string {
  const spots = seats.map((s) => s.spotNumber).filter((n): n is number => !!n);
  if (!spots.length) return '';
  if (spots.length === 1) return `Spot #${spots[0]}`;
  return `Spots ${spots.slice(0, -1).map((n) => `#${n}`).join(', ')} et #${spots[spots.length - 1]}`;
}

/** Toast de succès : message v1 pour la réservation entière, sinon la place annulée. */
export function cancelSuccessTitle(cancelled: CancelSeat[], all: CancelSeat[]): string {
  if (cancelled.length >= all.length) return 'La réservation a été annulée';
  return cancelled.length > 1 ? 'Les places de vos invités ont été annulées' : `${seatLabel(cancelled[0])} a été annulée`;
}

/**
 * Échec après au moins une annulation réussie : ce qui est annulé, ce qui ne l'est pas. null si rien n'a été
 * annulé (message d'erreur v1).
 */
export function partialCancelMessage(done: CancelSeat[], failed: CancelSeat[]): { title: string; description: string } | null {
  if (!done.length || !failed.length) return null;
  const doneText = done.map(seatLabel).join(' et ');
  const failedText = failed.map((s) => seatLabel(s).replace(/^La place/, 'la place').replace(/^Votre place/, 'votre place')).join(' et ');
  return {
    title: 'Annulation incomplète',
    description: `${doneText} ${done.length > 1 ? 'sont annulées' : 'est annulée'}, mais pas ${failedText}\u00a0: ${
      failed.length > 1 ? 'elles restent réservées' : 'elle reste réservée'
    }. Réessayez dans quelques instants.`,
  };
}
